// src/pages/Amenidades.js
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import {
    addDoc,
    collection,
    doc,
    getDoc,
    getDocs,
    onSnapshot,
    query,
    serverTimestamp,
    updateDoc,
    where,
} from 'firebase/firestore';
import { useAuth } from '../context/AuthContext';
import { useNotification } from '../context/NotificationContext';
import { useRestriccionMorosidad } from '../hooks/useRestriccionMorosidad';
import { db } from '../firebase';
import { AMENITIES_CATALOG } from '../constants/amenidades';
import { registerMovement } from '../utils/registerMovement';
import { FaClock, FaLock } from 'react-icons/fa';
import './Amenidades.css';
import './AmenidadesMobile.css';
import ConfirmDialog from '../components/ConfirmDialog';

const TIME_FORMAT = /^([01]\d|2[0-3]):([0-5]\d)$/;
const RANGE_FORMAT = /^([01]\d|2[0-3]):([0-5]\d)\s*-\s*([01]\d|2[0-3]):([0-5]\d)$/;
const WEEKDAY_BADGES = [
    { day: 1, label: 'Lun' },
    { day: 2, label: 'Mar' },
    { day: 3, label: 'Mié' },
    { day: 4, label: 'Jue' },
    { day: 5, label: 'Vie' },
    { day: 6, label: 'Sáb' },
    { day: 0, label: 'Dom' },
];
const ADMIN_ROLES = ['admin', 'admin_fracc', 'administrador', 'manager', 'platformadmin', 'plataforma', 'superadmin'];

const pad = (value) => String(value).padStart(2, '0');

const normalizeSlot = (slot) => {
    if (typeof slot !== 'string') {
        return '';
    }
    const trimmed = slot.trim();
    if (!trimmed) {
        return '';
    }

    const withNormalizedSeparator = trimmed.replace(/\s*-\s*/g, ' - ');
    const rangeMatch = withNormalizedSeparator.match(RANGE_FORMAT);
    if (rangeMatch) {
        const [, startHour, startMinutes, endHour, endMinutes] = rangeMatch;
        const start = `${pad(startHour)}:${pad(startMinutes)}`;
        const end = `${pad(endHour)}:${pad(endMinutes)}`;
        if (start === end) {
            return start;
        }
        return `${start} - ${end}`;
    }

    const singleMatch = trimmed.match(TIME_FORMAT);
    if (singleMatch) {
        const [, hour, minutes] = singleMatch;
        return `${pad(hour)}:${pad(minutes)}`;
    }

    const segments = trimmed.split('-').map((segment) => segment.trim());
    if (segments.length === 2 && TIME_FORMAT.test(segments[0]) && TIME_FORMAT.test(segments[1])) {
        return `${segments[0]} - ${segments[1]}`;
    }

    return trimmed;
};

const toRangeSlot = (value) => {
    if (typeof value !== 'string') {
        return null;
    }
    const normalized = normalizeSlot(value);
    const rangeMatch = normalized.match(RANGE_FORMAT);
    if (rangeMatch) {
        return normalized;
    }
    const singleMatch = normalized.match(TIME_FORMAT);
    if (!singleMatch) {
        return null;
    }
    const hour = Number(singleMatch[1]);
    const minutes = Number(singleMatch[2]);
    const endHour = (hour + 1) % 24;
    const start = `${pad(hour)}:${pad(minutes)}`;
    const end = `${pad(endHour)}:${pad(minutes)}`;
    if (start === end) {
        return null;
    }
    return `${start} - ${end}`;
};

const DEFAULT_START_HOUR = 9;
const DEFAULT_END_HOUR = 18;

const coerceScheduleArray = (value) => {
    if (Array.isArray(value)) {
        return value;
    }
    if (typeof value === 'string') {
        return [value];
    }
    if (value && typeof value === 'object') {
        if (Array.isArray(value.slots)) {
            return value.slots;
        }
        if (Array.isArray(value.horarios)) {
            return value.horarios;
        }
        const nestedArrays = Object.values(value)
            .filter((entry) => Array.isArray(entry))
            .flat();
        if (nestedArrays.length > 0) {
            return nestedArrays;
        }
    }
    return [];
};

const sanitizeScheduleList = (value) => {
    const list = coerceScheduleArray(value);
    if (list.length === 0) {
        return [];
    }
    const unique = Array.from(
        new Set(
            list
                .map((slot) => toRangeSlot(slot) || normalizeSlot(slot))
                .filter(Boolean),
        ),
    );
    unique.sort((a, b) => a.slice(0, 5).localeCompare(b.slice(0, 5)));
    return unique;
};

const generateDefaultHourlySlots = () => {
    const slots = [];
    for (let hour = DEFAULT_START_HOUR; hour < DEFAULT_END_HOUR; hour += 1) {
        const start = `${pad(hour)}:00`;
        const end = `${pad(hour + 1)}:00`;
        slots.push(`${start} - ${end}`);
    }
    return slots;
};

const sanitizeScheduleMap = (input) => {
    if (!input || typeof input !== 'object') {
        return {};
    }
    return Object.entries(input).reduce((acc, [amenidadId, value]) => {
        acc[amenidadId] = sanitizeScheduleList(value);
        return acc;
    }, {});
};

const getFraccionamientoId = (userData) => userData?.fraccionamientoId || userData?.fraccionamientoTd || null;

const extractNombre = (source) => {
    if (!source || typeof source !== 'object') {
        return null;
    }
    const directCandidates = [
        source.creadoPorNombre,
        source.nombreCompleto,
        source.nombre,
        source.displayName,
        source.nombre_apellido,
        source.fullName,
    ];
    const direct = directCandidates.find((candidate) => typeof candidate === 'string' && candidate.trim().length > 0);
    if (direct) {
        return direct.trim();
    }
    const composed = [
        typeof source.nombres === 'string' ? source.nombres.trim() : '',
        typeof source.apellidos === 'string' ? source.apellidos.trim() : '',
    ].filter(Boolean).join(' ');
    return composed || null;
};

const extractCasaNumero = (source) => {
    if (!source || typeof source !== 'object') {
        return null;
    }
    const candidates = [
        source.casaNumero,
        source.numeroCasa,
        source.casa,
        source.casa_numero,
    ];
    for (let index = 0; index < candidates.length; index += 1) {
        const candidate = candidates[index];
        if (typeof candidate === 'string' && candidate.trim().length > 0) {
            return candidate.trim();
        }
        if (typeof candidate === 'number' && Number.isFinite(candidate)) {
            return String(candidate);
        }
    }
    return null;
};

const isValidDateInput = (value) => {
    if (typeof value !== 'string') {
        return false;
    }
    const match = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!match) {
        return false;
    }
    const date = new Date(`${value}T00:00:00`);
    return Number.isFinite(date.getTime());
};

const formatDateForInput = (date) => {
    const instance = date instanceof Date ? date : new Date(date);
    if (!Number.isFinite(instance.getTime())) {
        return '';
    }
    const year = instance.getFullYear();
    const month = `${instance.getMonth() + 1}`.padStart(2, '0');
    const day = `${instance.getDate()}`.padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const formatDateLabel = (dateString) => {
    if (!isValidDateInput(dateString)) {
        return dateString || '';
    }
    const [year, month, day] = dateString.split('-').map(Number);
    const date = new Date(year, (month || 1) - 1, day || 1);
    if (!Number.isFinite(date.getTime())) {
        return dateString;
    }
    const formatter = new Intl.DateTimeFormat('es-MX', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
    });
    const label = formatter.format(date);
    return label.charAt(0).toUpperCase() + label.slice(1);
};

const getWeekdayFromDate = (value) => {
    if (!isValidDateInput(value)) {
        return null;
    }
    const date = new Date(`${value}T00:00:00`);
    if (!Number.isFinite(date.getTime())) {
        return null;
    }
    return date.getDay();
};

const buildReservationTooltip = (reservation, isAdmin, currentUserId) => {
    if (!reservation) {
        return '';
    }

    if (!isAdmin && reservation.userId !== currentUserId) {
        return 'Ocupado';
    }

    const parts = [];
    if (reservation.userId === currentUserId) {
        parts.push('Mi Reserva');
    } else if (isAdmin && reservation.createdByName) {
        parts.push(`Residente: ${reservation.createdByName}`);
    }

    if (isAdmin && reservation.casaNumero) {
        parts.push(`Casa ${reservation.casaNumero}`);
    }

    if (reservation.horario) {
        parts.push(`Horario ${reservation.horario}`);
    }

    if (reservation.notas && (isAdmin || reservation.userId === currentUserId)) {
        parts.push(reservation.notas);
    }

    return parts.join(' · ');
};

const Amenidades = () => {
    const { userData, currentUser } = useAuth();
    const { showNotification } = useNotification();

    const fraccionamientoId = getFraccionamientoId(userData);
    const userRole = String(userData?.rol || '').toLowerCase();
    const isCaseta = userRole === 'caseta';
    const navigate = useNavigate();
    const { isTabRestricted, isSubSectionRestricted } = useRestriccionMorosidad();
    const isResidentUser = ['user', 'residente', 'inquilino', 'propietario'].includes(userRole);
    const isReservaBloqueada = isResidentUser && (isTabRestricted('amenidades') || isSubSectionRestricted('amenidades', 'reservar_amenidades'));
    const isCalendarioBloqueado = isResidentUser && (isTabRestricted('amenidades') || isSubSectionRestricted('amenidades', 'calendario_disponibilidad'));
    const isMisReservasBloqueada = isResidentUser && (isTabRestricted('amenidades') || isSubSectionRestricted('amenidades', 'mis_reservas'));

    const mostrarAlertaRestriccion = useCallback((mensaje = 'La reserva de amenidades está temporalmente inhabilitada por adeudo pendiente. Puedes acudir a Finanzas para regularizar tu cuenta.') => {
        Swal.fire({
            title: 'Servicio Inhabilitado',
            text: mensaje,
            icon: 'warning',
            confirmButtonText: 'Ir a Finanzas',
            showCancelButton: true,
            cancelButtonText: 'Cerrar',
            confirmButtonColor: '#0284c7'
        }).then((result) => {
            if (result.isConfirmed) {
                navigate('/finanzas');
            }
        });
    }, [navigate]);

    const [selectedDate, setSelectedDate] = useState(() => formatDateForInput(new Date()));
    const [amenidadesActivas, setAmenidadesActivas] = useState(undefined);
    const [amenidadesHorarios, setAmenidadesHorarios] = useState({});
    const [amenidadesHorariosConfigurados, setAmenidadesHorariosConfigurados] = useState([]);
    const [reservasHoy, setReservasHoy] = useState({});
    const [userReservations, setUserReservations] = useState({});
    const [selectedAmenidad, setSelectedAmenidad] = useState(null);
    const [preselectedHorario, setPreselectedHorario] = useState('');
    const [reservationToEdit, setReservationToEdit] = useState(null);
    const [modalMode, setModalMode] = useState('create');
    const [isModalOpen, setModalOpen] = useState(false);
    const [hasUsedCancellation, setHasUsedCancellation] = useState(false);
    const [reservationManagerAmenidad, setReservationManagerAmenidad] = useState(null);
    const [adminReservationTarget, setAdminReservationTarget] = useState(null);
    const [calendarOpen, setCalendarOpen] = useState(false);
    const usuariosReservaCacheRef = useRef({});
    const lastFraccionamientoRef = useRef(null);

    const currentUserId = useMemo(() => {
        if (typeof userData?.uid === 'string' && userData.uid.trim().length > 0) {
            return userData.uid.trim();
        }
        if (typeof currentUser?.uid === 'string' && currentUser.uid.trim().length > 0) {
            return currentUser.uid.trim();
        }
        return null;
    }, [currentUser?.uid, userData?.uid]);

    const roles = useMemo(() => {
        const roleCandidates = [
            typeof userData?.rol === 'string' ? userData.rol : null,
            typeof userData?.role === 'string' ? userData.role : null,
            ...(Array.isArray(userData?.roles) ? userData.roles : []),
        ];
        return new Set(
            roleCandidates
                .filter((role) => typeof role === 'string')
                .map((role) => role.toLowerCase()),
        );
    }, [userData]);

    const configuredScheduleIds = useMemo(() => new Set(amenidadesHorariosConfigurados), [amenidadesHorariosConfigurados]);

    const isSuperAdmin = roles.has('superadmin');
    const isAdmin = ADMIN_ROLES.some((role) => roles.has(role));
    const isLoadingAmenidades = amenidadesActivas === undefined;

    const selectedDateLabel = useMemo(() => formatDateLabel(selectedDate), [selectedDate]);
    const activeWeekday = useMemo(() => getWeekdayFromDate(selectedDate), [selectedDate]);

    const amenitiesCatalogById = useMemo(() => {
        const map = new Map();
        AMENITIES_CATALOG.forEach((amenidad) => {
            map.set(amenidad.id, amenidad);
        });
        return map;
    }, []);

    const amenitiesToDisplay = useMemo(() => {
        const activeIds = Array.isArray(amenidadesActivas) && amenidadesActivas.length > 0
            ? amenidadesActivas
            : AMENITIES_CATALOG.map((amenidad) => amenidad.id);
        const uniqueIds = Array.from(new Set(activeIds));

        return uniqueIds.map((amenidadId) => {
            const base = amenitiesCatalogById.get(amenidadId) || {};
            const IconComponent = base.Icon;
            const rawSlots = Array.isArray(amenidadesHorarios[amenidadId]) ? amenidadesHorarios[amenidadId] : [];
            const isConfigured = configuredScheduleIds.has(amenidadId);
            const supportsDefaultSlots = ['reservable', 'disponibilidad'].includes(base.type);
            const horarios = rawSlots.length > 0
                ? rawSlots
                : (!isConfigured && supportsDefaultSlots ? generateDefaultHourlySlots() : rawSlots);
            return {
                id: amenidadId,
                name: base.name || amenidadId,
                type: base.type || 'reservable',
                icon: IconComponent ? <IconComponent /> : (base.icon || '🏠'),
                horarios,
            };
        });
    }, [amenidadesActivas, amenidadesHorarios, amenitiesCatalogById, configuredScheduleIds]);

    useEffect(() => {
        if (typeof userData?.amenidadCancelacionUsada === 'boolean') {
            setHasUsedCancellation(userData.amenidadCancelacionUsada);
            return;
        }
        if (!userData?.uid) {
            setHasUsedCancellation(false);
            return;
        }

        let isMounted = true;
        const fetchCancellationFlag = async () => {
            try {
                const userDocRef = doc(db, 'usuarios', userData.uid);
                const userSnapshot = await getDoc(userDocRef);
                if (userSnapshot.exists() && isMounted) {
                    const data = userSnapshot.data();
                    if (typeof data?.amenidadCancelacionUsada === 'boolean') {
                        setHasUsedCancellation(data.amenidadCancelacionUsada);
                    }
                }
            } catch (error) {
                console.error('Amenidades: no se pudo obtener el estado de cancelación del usuario', error);
            }
        };

        fetchCancellationFlag();

        return () => {
            isMounted = false;
        };
    }, [userData?.amenidadCancelacionUsada, userData?.uid]);

    useEffect(() => {
        setAdminReservationTarget(null);
    }, [isAdmin, selectedDate, fraccionamientoId]);

    useEffect(() => {
        if (!fraccionamientoId) {
            setAmenidadesActivas([]);
            setAmenidadesHorarios({});
            setAmenidadesHorariosConfigurados([]);
            return undefined;
        }

        setAmenidadesActivas(undefined);
        setAmenidadesHorarios({});
        setAmenidadesHorariosConfigurados([]);
        const fraccionamientoRef = doc(db, 'fraccionamientos', fraccionamientoId);
        const unsubscribe = onSnapshot(fraccionamientoRef, (snapshot) => {
            const data = snapshot.data() || {};
            const activeAmenities = Array.isArray(data.amenidadesActivas)
                ? data.amenidadesActivas.filter((amenidadId) => typeof amenidadId === 'string' && amenidadId.trim().length > 0)
                : [];
            const configuredSchedules = sanitizeScheduleMap(data.amenidadesHorarios);
            const configuredIds = data.amenidadesHorarios && typeof data.amenidadesHorarios === 'object'
                ? Object.keys(data.amenidadesHorarios).filter((amenidadId) => typeof amenidadId === 'string' && amenidadId.trim().length > 0)
                : [];
            setAmenidadesActivas(activeAmenities);
            setAmenidadesHorarios(configuredSchedules);
            setAmenidadesHorariosConfigurados(configuredIds);
        }, (error) => {
            console.error('Error al cargar las amenidades activas del fraccionamiento:', error);
            setAmenidadesActivas([]);
            setAmenidadesHorarios({});
            setAmenidadesHorariosConfigurados([]);
        });

        return () => unsubscribe();
    }, [fraccionamientoId]);

    useEffect(() => {
        if (!fraccionamientoId || !selectedDate) {
            setReservasHoy({});
            return undefined;
        }

        if (lastFraccionamientoRef.current !== fraccionamientoId) {
            usuariosReservaCacheRef.current = {};
            lastFraccionamientoRef.current = fraccionamientoId;
        }

        const reservasRef = collection(db, 'fraccionamientos', fraccionamientoId, 'reservasAmenidades');
        const reservasQuery = query(
            reservasRef,
            where('fecha', '==', selectedDate),
            where('estado', '==', 'activo'),
        );

        let isActive = true;

        const fetchHouseNumberByUserId = async (userId) => {
            if (!userId) {
                console.log('[fetchHouseNumberByUserId] Sin userId');
                return null;
            }

            console.log('[fetchHouseNumberByUserId] Iniciando búsqueda para userId:', userId);

            try {
                // PASO 1: Obtener el fraccionamientoId del usuario
                console.log('[fetchHouseNumberByUserId] PASO 1: Obteniendo datos del usuario...');
                const userDoc = await getDoc(doc(db, 'usuarios', userId));

                if (!userDoc.exists()) {
                    console.log('[fetchHouseNumberByUserId] Usuario no encontrado:', userId);
                    return null;
                }

                const userData = userDoc.data();
                const userFraccionamientoId = userData.fraccionamientoId || userData.fraccionamientoTd;
                console.log('[fetchHouseNumberByUserId] Datos del usuario:', { userId, fraccionamientoId: userFraccionamientoId });

                if (!userFraccionamientoId) {
                    console.log('[fetchHouseNumberByUserId] Usuario sin fraccionamientoId asignado');
                    return null;
                }

                // PASO 2: Buscar en casas usando el uid del usuario
                console.log('[fetchHouseNumberByUserId] PASO 2: Buscando en casas con uid:', userId);
                const casasRef = collection(db, 'fraccionamientos', userFraccionamientoId, 'casas');
                const q = query(casasRef, where('uid', '==', userId));
                const casasSnapshot = await getDocs(q);

                console.log('[fetchHouseNumberByUserId] Casas encontradas:', casasSnapshot.size);

                // PASO 3: Extraer el número de vivienda
                if (casasSnapshot.size > 0) {
                    const casaDoc = casasSnapshot.docs[0];
                    const casaData = casaDoc.data();
                    const numeroVivienda = casaData.numero;

                    console.log('[fetchHouseNumberByUserId] ¡ENCONTRADO!:', { userId, fraccionamientoId: userFraccionamientoId, numero: numeroVivienda });
                    return String(numeroVivienda);
                }

                console.log('[fetchHouseNumberByUserId] No se encontró casa con uid:', userId);
                return null;
            } catch (error) {
                console.error('[fetchHouseNumberByUserId] Error:', error);
                return null;
            }
        };

        const fetchUsersHouseNumbers = async (userIds) => {
            const results = {};
            await Promise.all(userIds.map(async (userId) => {
                try {
                    const userDocSnap = await getDoc(doc(db, 'usuarios', userId));
                    if (userDocSnap.exists()) {
                        const info = userDocSnap.data();
                        results[userId] = {
                            casaNumero: extractCasaNumero(info),
                            nombre: extractNombre(info),
                        };
                    } else {
                        results[userId] = {
                            casaNumero: null,
                            nombre: null,
                        };
                    }
                } catch (fetchError) {
                    console.error(`Error al obtener datos del usuario ${userId}:`, fetchError);
                }
            }));
            return results;
        };

        const processSnapshot = async (snapshot) => {
            const cache = usuariosReservaCacheRef.current;
            const reservations = [];
            const missingUserIds = new Set();

            snapshot.forEach((docSnap) => {
                const data = docSnap.data();
                const amenidadId = data?.amenidadId;
                const horario = normalizeSlot(data?.horario);
                if (!amenidadId || !horario) {
                    return;
                }
                const userId = typeof data?.creadoPorUid === 'string' && data.creadoPorUid.trim().length > 0
                    ? data.creadoPorUid
                    : null;
                const casaNumero = extractCasaNumero(data);
                if (!casaNumero && userId && !cache[userId]) {
                    missingUserIds.add(userId);
                }

                reservations.push({
                    reservationId: docSnap.id,
                    amenidadId,
                    horario,
                    userId,
                    casaNumero,
                    createdByName: extractNombre(data),
                    fecha: typeof data?.fecha === 'string' ? data.fecha : null,
                    notas: typeof data?.notas === 'string' ? data.notas : null,
                    area: typeof data?.area === 'string' ? data.area : null,
                });
            });

            let fetchedUsers = {};
            if (missingUserIds.size > 0) {
                fetchedUsers = await fetchUsersHouseNumbers(Array.from(missingUserIds));
            }

            if (!isActive) {
                return;
            }

            if (Object.keys(fetchedUsers).length > 0) {
                usuariosReservaCacheRef.current = {
                    ...usuariosReservaCacheRef.current,
                    ...fetchedUsers,
                };
            }

            const combinedCache = usuariosReservaCacheRef.current;
            const grouped = {};

            // Buscar números de casa faltantes usando el userId
            const reservationsNeedingHouseNumber = reservations.filter(
                (entry) => !entry.casaNumero && !combinedCache[entry.userId]?.casaNumero && entry.userId
            );

            console.log('[Amenidades] Reservas que necesitan número de casa:', reservationsNeedingHouseNumber);

            const houseNumbersByUserId = {};
            if (reservationsNeedingHouseNumber.length > 0) {
                console.log('[Amenidades] Buscando números de casa por userId...');
                await Promise.all(
                    reservationsNeedingHouseNumber.map(async (entry) => {
                        if (!houseNumbersByUserId[entry.userId]) {
                            console.log('[Amenidades] Buscando casa para userId:', entry.userId);
                            const houseNum = await fetchHouseNumberByUserId(entry.userId);
                            console.log('[Amenidades] Resultado búsqueda:', { userId: entry.userId, casaEncontrada: houseNum });
                            if (houseNum) {
                                houseNumbersByUserId[entry.userId] = houseNum;
                            }
                        }
                    })
                );
                console.log('[Amenidades] Números de casa encontrados:', houseNumbersByUserId);
            }

            reservations.forEach((entry) => {
                const { amenidadId, horario } = entry;
                if (!grouped[amenidadId]) {
                    grouped[amenidadId] = new Map();
                }
                const scheduleMap = grouped[amenidadId];

                // Intentar obtener casaNumero de múltiples fuentes
                let mergedCasaNumero = entry.casaNumero || combinedCache[entry.userId]?.casaNumero;

                // Si aún no tenemos número de casa, buscar por userId
                if (!mergedCasaNumero && entry.userId) {
                    mergedCasaNumero = houseNumbersByUserId[entry.userId] || null;
                    console.log('[Amenidades] Usando casa por userId:', { userId: entry.userId, casa: mergedCasaNumero });
                }

                const mergedNombre = entry.createdByName || combinedCache[entry.userId]?.nombre || null;
                scheduleMap.set(horario, {
                    reservationId: entry.reservationId,
                    horario,
                    userId: entry.userId,
                    casaNumero: mergedCasaNumero,
                    createdByName: mergedNombre,
                    fecha: entry.fecha,
                    notas: entry.notas,
                    area: entry.area,
                });
            });

            const final = Object.entries(grouped).reduce((acc, [amenidadId, scheduleMap]) => {
                const ordered = Array.from(scheduleMap.values())
                    .sort((a, b) => a.horario.slice(0, 5).localeCompare(b.horario.slice(0, 5)));
                acc[amenidadId] = ordered;
                return acc;
            }, {});

            setReservasHoy(final);
        };

        const unsubscribe = onSnapshot(reservasQuery, (snapshot) => {
            processSnapshot(snapshot).catch((error) => {
                console.error('Error al procesar reservas de amenidades:', error);
                if (isActive) {
                    setReservasHoy({});
                }
            });
        }, (error) => {
            console.error('Error al cargar la disponibilidad de amenidades:', error);
            if (isActive) {
                setReservasHoy({});
            }
        });

        return () => {
            isActive = false;
            unsubscribe();
        };
    }, [fraccionamientoId, selectedDate]);

    useEffect(() => {
        if (!fraccionamientoId || !currentUserId) {
            setUserReservations({});
            return undefined;
        }

        const reservasRef = collection(db, 'fraccionamientos', fraccionamientoId, 'reservasAmenidades');
        const userQuery = query(
            reservasRef,
            where('creadoPorUid', '==', currentUserId),
            where('estado', '==', 'activo'),
        );

        const unsubscribe = onSnapshot(userQuery, (snapshot) => {
            const counters = {};
            snapshot.forEach((docSnap) => {
                const data = docSnap.data();
                const amenidadId = data?.amenidadId;
                if (!amenidadId) {
                    return;
                }
                counters[amenidadId] = (counters[amenidadId] || 0) + 1;
            });
            setUserReservations(counters);
        }, (error) => {
            console.error('Error al cargar las reservas del usuario:', error);
            setUserReservations({});
        });

        return () => unsubscribe();
    }, [fraccionamientoId, currentUserId]);

    const handleCancelReservation = useCallback(async (amenidad, reservationInfo) => {
        if (!fraccionamientoId || !reservationInfo?.reservationId) {
            showNotification('No fue posible identificar la reserva a cancelar.', 'error');
            return;
        }

        if (!isAdmin && hasUsedCancellation) {
            showNotification('Ya usaste tu cancelación disponible. Contacta a la administración para más ayuda.', 'info');
            return;
        }

        try {
            const reservaDocRef = doc(db, 'fraccionamientos', fraccionamientoId, 'reservasAmenidades', reservationInfo.reservationId);
            await updateDoc(reservaDocRef, {
                estado: 'cancelado',
                canceladoEn: serverTimestamp(),
                canceladoPorUid: userData?.uid || null,
                canceladoPorNombre: extractNombre(userData) || userData?.displayName || null,
            });

            try {
                await registerMovement({
                    fraccionamientoId,
                    titulo: `Cancelación reserva: ${amenidad?.name || amenidad?.id}`,
                    descripcion: `Se canceló el horario ${reservationInfo.horario} del ${formatDateLabel(reservationInfo.fecha || selectedDate)}.`,
                    categoria: 'Amenidades',
                    tipoMovimiento: 'cancelacion_reserva_amenidad',
                    origen: 'amenidades',
                    metadata: {
                        amenidadId: amenidad?.id || null,
                        amenidadNombre: amenidad?.name || null,
                        horarioCancelado: reservationInfo.horario || null,
                        fechaCancelada: reservationInfo.fecha || selectedDate || null,
                        reservaId: reservationInfo.reservationId,
                    },
                    userData,
                    currentUser,
                });
            } catch (logError) {
                console.error('Amenidades: error al registrar el movimiento de cancelación', logError);
            }

            if (!isAdmin && !hasUsedCancellation && userData?.uid) {
                try {
                    const usuarioRef = doc(db, 'usuarios', userData.uid);
                    await updateDoc(usuarioRef, {
                        amenidadCancelacionUsada: true,
                        amenidadCancelacionUsadaEn: serverTimestamp(),
                    });
                } catch (userUpdateError) {
                    console.error('Amenidades: no se pudo actualizar el estado de cancelación del usuario', userUpdateError);
                }
                setHasUsedCancellation(true);
            }

            if (amenidad?.id && reservationInfo?.userId && reservationInfo.userId === currentUserId) {
                setUserReservations((prev) => {
                    const nextCount = Math.max((prev[amenidad.id] || 1) - 1, 0);
                    return {
                        ...prev,
                        [amenidad.id]: nextCount,
                    };
                });
            }

            if (isAdmin) {
                setAdminReservationTarget(null);
            }

            showNotification('La reserva se canceló correctamente.', 'success');
        } catch (error) {
            console.error('Error al cancelar la reserva de amenidad:', error);
            showNotification('No fue posible cancelar la reserva. Intenta más tarde o contacta a la administración.', 'error');
        }
    }, [currentUser, currentUserId, fraccionamientoId, hasUsedCancellation, isAdmin, selectedDate, showNotification, userData]);

    const handleOpenModal = useCallback((amenidad, horarioPreseleccionado = '', options = {}) => {
        const resolvedMode = options.mode === 'edit' ? 'edit' : 'create';
        if (isReservaBloqueada && resolvedMode !== 'view') {
            mostrarAlertaRestriccion('La reserva de amenidades está temporalmente inhabilitada por adeudo pendiente. Puedes acudir a Finanzas para regularizar tu cuenta.');
            return;
        }
        setSelectedAmenidad(amenidad);
        setModalMode(resolvedMode);
        setReservationToEdit(options.reservation || null);
        setModalOpen(true);

        const resolvedHorario = options?.reservation?.horario || horarioPreseleccionado;
        const normalizedPreselected = normalizeSlot(resolvedHorario) || resolvedHorario || '';
        setPreselectedHorario(normalizedPreselected);
    }, [isReservaBloqueada, mostrarAlertaRestriccion]);

    const handleCloseModal = useCallback(() => {
        setModalOpen(false);
        setSelectedAmenidad(null);
        setPreselectedHorario('');
        setReservationToEdit(null);
        setModalMode('create');
    }, []);

    const handleOpenReservationManager = useCallback((amenidad) => {
        setReservationManagerAmenidad(amenidad);
    }, []);

    const handleCloseReservationManager = useCallback(() => {
        setReservationManagerAmenidad(null);
    }, []);

    const handleStartEditReservation = useCallback(async (amenidad, reservation) => {
        if (!fraccionamientoId || !reservation?.reservationId) {
            showNotification('No se pudo preparar la edición de la reserva.', 'error');
            return;
        }

        try {
            const reservaRef = doc(db, 'fraccionamientos', fraccionamientoId, 'reservasAmenidades', reservation.reservationId);
            const snapshot = await getDoc(reservaRef);
            if (!snapshot.exists()) {
                showNotification('La reserva seleccionada ya no está disponible.', 'warning');
                return;
            }
            const data = snapshot.data() || {};
            const normalizedHorario = normalizeSlot(data?.horario) || reservation.horario || '';
            handleOpenModal(amenidad, normalizedHorario, {
                mode: 'edit',
                reservation: {
                    id: snapshot.id,
                    reservationId: snapshot.id,
                    horario: normalizedHorario,
                    fecha: typeof data?.fecha === 'string' ? data.fecha : reservation.fecha || selectedDate,
                    notas: typeof data?.notas === 'string' ? data.notas : '',
                    area: typeof data?.area === 'string' ? data.area : amenidad?.name || '',
                    userId: data?.creadoPorUid || reservation.userId || null,
                },
            });
            setReservationManagerAmenidad(null);
        } catch (error) {
            console.error('Error al preparar la edición de la reserva:', error);
            showNotification('No se pudo preparar la edición de la reserva. Intenta nuevamente.', 'error');
        }
    }, [fraccionamientoId, handleOpenModal, selectedDate, showNotification]);

    const handleUpdateReserva = useCallback(async (payload) => {
        if (!fraccionamientoId || !payload?.reservationId) {
            showNotification('No se pudo identificar la reserva a actualizar.', 'error');
            return false;
        }

        const normalizedHorario = normalizeSlot(payload.horario) || payload.horario;
        if (!normalizedHorario) {
            showNotification('Selecciona un horario válido para actualizar la reserva.', 'warning');
            return false;
        }

        try {
            const reservasRef = collection(db, 'fraccionamientos', fraccionamientoId, 'reservasAmenidades');
            // Validar que el residente no reserve la misma amenidad más de una vez en el mismo día
            if (!isAdmin) {
                const sameAmenidadSameDayQuery = query(
                    reservasRef,
                    where('amenidadId', '==', payload.amenidadId),
                    where('fecha', '==', payload.fecha),
                    where('creadoPorUid', '==', currentUserId || ''),
                    where('estado', '==', 'activo'),
                );
                const sameAmenidadSameDaySnapshot = await getDocs(sameAmenidadSameDayQuery);

                // Verificar si hay otra reserva que NO sea la actual
                const hasOtherReservation = sameAmenidadSameDaySnapshot.docs.some((docSnap) => {
                    return docSnap.id !== payload.reservationId; // Excluir la reserva que se está editando
                });

                if (hasOtherReservation) {
                    showNotification('Solo puedes reservar esta amenidad una vez por día.', 'warning');
                    return false;
                }
            }

            const duplicateQuery = query(
                reservasRef,
                where('amenidadId', '==', payload.amenidadId),
                where('fecha', '==', payload.fecha),
                where('estado', '==', 'activo'),
            );
            const duplicateSnapshot = await getDocs(duplicateQuery);
            const hasConflict = duplicateSnapshot.docs.some((docSnap) => {
                if (docSnap.id === payload.reservationId) {
                    return false;
                }
                const existing = normalizeSlot(docSnap.data()?.horario) || docSnap.data()?.horario || '';
                return existing === normalizedHorario;
            });
            if (hasConflict) {
                showNotification('Ese horario ya está reservado. Elige otro horario disponible.', 'warning');
                return false;
            }

            if (payload.userId && payload.userId !== (currentUserId || '') && !isSuperAdmin) {
                showNotification('No tienes permisos para editar esta reserva.', 'error');
                return false;
            }

            const subjectUserId = payload.userId || currentUserId || '';
            if (subjectUserId) {
                const sameTimeQuery = query(
                    reservasRef,
                    where('creadoPorUid', '==', subjectUserId),
                    where('fecha', '==', payload.fecha),
                    where('estado', '==', 'activo'),
                );
                const sameTimeSnapshot = await getDocs(sameTimeQuery);
                const overlappingReservation = sameTimeSnapshot.docs.some((docSnap) => {
                    if (docSnap.id === payload.reservationId) {
                        return false;
                    }
                    const existing = normalizeSlot(docSnap.data()?.horario) || docSnap.data()?.horario || '';
                    return existing === normalizedHorario;
                });
                if (overlappingReservation) {
                    showNotification('No puedes reservar más de una amenidad en el mismo horario.', 'warning');
                    return false;
                }
            }

            await updateDoc(doc(db, 'fraccionamientos', fraccionamientoId, 'reservasAmenidades', payload.reservationId), {
                fecha: payload.fecha,
                horario: normalizedHorario,
                notas: payload.notas || null,
                actualizadoEn: serverTimestamp(),
                actualizadoPorUid: userData?.uid || null,
                actualizadoPorNombre: extractNombre(userData) || userData?.displayName || null,
            });

            try {
                await registerMovement({
                    fraccionamientoId,
                    titulo: `Actualización reserva: ${payload.amenidadNombre || payload.amenidadId}`,
                    descripcion: `Se actualizó al horario ${normalizedHorario} del ${formatDateLabel(payload.fecha)}.`,
                    categoria: 'Amenidades',
                    tipoMovimiento: 'actualizacion_reserva_amenidad',
                    origen: 'amenidades',
                    metadata: {
                        amenidadId: payload.amenidadId,
                        amenidadNombre: payload.amenidadNombre || null,
                        horarioReserva: normalizedHorario,
                        fechaReserva: payload.fecha,
                        reservaId: payload.reservationId,
                    },
                    userData,
                    currentUser,
                });
            } catch (logError) {
                console.error('Amenidades: error al registrar movimiento de actualización', logError);
            }

            showNotification('La reserva se actualizó correctamente.', 'success');
            return true;
        } catch (error) {
            console.error('Error al actualizar la reserva de amenidad:', error);
            showNotification('No se pudo actualizar la reserva. Intenta nuevamente.', 'error');
            return false;
        }
    }, [currentUser, currentUserId, fraccionamientoId, isSuperAdmin, showNotification, userData]);

    const handleSubmitReserva = useCallback(async (payload) => {
        if (!fraccionamientoId) {
            showNotification('No se pudo identificar el fraccionamiento para registrar la reserva.', 'error');
            return false;
        }

        if (!payload?.amenidadId || !payload?.fecha || !payload?.horario) {
            showNotification('Faltan datos para completar la reserva.', 'error');
            return false;
        }

        const reservasRef = collection(db, 'fraccionamientos', fraccionamientoId, 'reservasAmenidades');

        try {
            if (isReservaBloqueada) {
                mostrarAlertaRestriccion('La reserva de amenidades está temporalmente inhabilitada por adeudo pendiente. Regulariza tu cuenta en Finanzas.');
                return false;
            }

            const casaNumeroActual = extractCasaNumero(userData);
            const nombreActual = extractNombre(userData);
            const normalizedHorario = normalizeSlot(payload.horario) || payload.horario;

            // Validar que los residentes solo puedan reservar HOY, MAÑANA y 2 DÍAS DESPUÉS
            if (!isAdmin) {
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                const tomorrow = new Date(today);
                tomorrow.setDate(tomorrow.getDate() + 1);
                const twoDaysLater = new Date(today);
                twoDaysLater.setDate(twoDaysLater.getDate() + 2);
                const threeDaysLater = new Date(today);
                threeDaysLater.setDate(threeDaysLater.getDate() + 3);

                const reservationDate = new Date(`${payload.fecha}T00:00:00`);

                // No permitir más de 2 días de antelación
                if (reservationDate >= threeDaysLater) {
                    showNotification('No puedes seleccionar amenidades con más de 2 días de antelación.', 'warning');
                    return false;
                }

                // Validar que el residente no reserve la misma amenidad más de una vez en el mismo día
                const sameAmenidadSameDayQuery = query(
                    reservasRef,
                    where('amenidadId', '==', payload.amenidadId),
                    where('fecha', '==', payload.fecha),
                    where('creadoPorUid', '==', currentUserId || ''),
                    where('estado', '==', 'activo'),
                );
                const sameAmenidadSameDaySnapshot = await getDocs(sameAmenidadSameDayQuery);

                // Debug: Log para verificar qué se está encontrando
                console.log('[Amenidades Debug] Validando reserva de amenidad:', {
                    amenidadId: payload.amenidadId,
                    fechaBuscada: payload.fecha,
                    userId: currentUserId,
                    reservasEncontradas: sameAmenidadSameDaySnapshot.size,
                });

                if (!sameAmenidadSameDaySnapshot.empty) {
                    // Log de las reservas encontradas para debug
                    sameAmenidadSameDaySnapshot.docs.forEach((doc) => {
                        console.log('[Amenidades Debug] Reserva encontrada:', {
                            id: doc.id,
                            fecha: doc.data().fecha,
                            amenidadId: doc.data().amenidadId,
                            horario: doc.data().horario,
                        });
                    });
                    showNotification('Solo puedes reservar esta amenidad una vez por día.', 'warning');
                    return false;
                }
            }

            // Validar que no se reserve el mismo horario para múltiples amenidades en el mismo día
            // Un residente solo puede tener 1 horario por amenidad, y no puede repetir el mismo horario en otra amenidad el mismo día
            const creatorId = currentUserId || '';
            if (creatorId) {
                const userReservasQuery = query(
                    reservasRef,
                    where('creadoPorUid', '==', creatorId),
                    where('fecha', '==', payload.fecha),
                    where('estado', '==', 'activo'),
                );
                const userReservasSnapshot = await getDocs(userReservasQuery);

                // Verificar si el mismo horario ya está reservado para otra amenidad
                const sameHorarioOtherAmenidad = userReservasSnapshot.docs.some((docSnap) => {
                    const existingData = docSnap.data();
                    const existing = normalizeSlot(existingData?.horario) || existingData?.horario || '';
                    const existingAmenidadId = existingData?.amenidadId;
                    // Si el horario es igual Y es diferente amenidad, no permitir
                    return existing === normalizedHorario && existingAmenidadId !== payload.amenidadId;
                });

                if (sameHorarioOtherAmenidad) {
                    showNotification('Ya tienes reservado ese horario para otra amenidad en esta fecha. Selecciona un horario disponible.', 'warning');
                    return false;
                }
            }

            const duplicateQuery = query(
                reservasRef,
                where('amenidadId', '==', payload.amenidadId),
                where('fecha', '==', payload.fecha),
                where('estado', '==', 'activo'),
            );
            const duplicateSnapshot = await getDocs(duplicateQuery);
            const horarioInicio = (typeof normalizedHorario === 'string' && normalizedHorario.length >= 5)
                ? normalizedHorario.slice(0, 5)
                : null;
            const hasConflict = duplicateSnapshot.docs.some((docSnap) => {
                const existingRaw = docSnap.data()?.horario;
                const existing = normalizeSlot(existingRaw) || existingRaw || '';
                if (existing === normalizedHorario) {
                    return true;
                }
                if (horarioInicio && existing === horarioInicio) {
                    return true;
                }
                return false;
            });
            if (hasConflict) {
                showNotification('Ese horario ya está reservado para esta amenidad. Intenta con otro horario disponible.', 'warning');
                return false;
            }

            await addDoc(reservasRef, {
                amenidadId: payload.amenidadId,
                amenidadNombre: payload.amenidadNombre || null,
                fecha: payload.fecha,
                horario: normalizedHorario,
                area: payload.area || null,
                notas: payload.notas || null,
                creadoPorUid: currentUserId || null,
                creadoPorNombre: nombreActual || userData?.nombre || userData?.nombreCompleto || userData?.displayName || null,
                casaNumero: casaNumeroActual,
                creadoEn: serverTimestamp(),
                estado: 'activo',
            });

            try {
                await registerMovement({
                    fraccionamientoId,
                    titulo: `Reserva amenidad: ${payload.amenidadNombre || payload.amenidadId}`,
                    descripcion: `Fecha ${payload.fecha} · Horario ${normalizedHorario}`,
                    categoria: 'Amenidades',
                    tipoMovimiento: 'reserva_amenidad',
                    origen: 'amenidades',
                    metadata: {
                        amenidadId: payload.amenidadId,
                        amenidadNombre: payload.amenidadNombre || null,
                        fechaReserva: payload.fecha,
                        horarioReserva: normalizedHorario,
                        casaNumero: casaNumeroActual,
                        notas: payload.notas || null,
                        area: payload.area || null,
                    },
                    userData,
                    currentUser,
                });
            } catch (logError) {
                console.error('Amenidades: error al registrar movimiento de reserva', logError);
            }

            if (!isAdmin && payload.amenidadId) {
                setUserReservations((prev) => ({
                    ...prev,
                    [payload.amenidadId]: (prev[payload.amenidadId] || 0) + 1,
                }));
            }

            if (isAdmin) {
                showNotification('Reserva registrada correctamente.', 'success');
            } else {
                showNotification('Tu reserva quedó registrada. Para cambios futuros, comunícate con la administración.', 'success');
            }
            return true;
        } catch (error) {
            console.error('Error al registrar la reserva de amenidad:', error);
            showNotification('No se pudo registrar la reserva. Intenta nuevamente.', 'error');
            return false;
        }
    }, [currentUser, currentUserId, fraccionamientoId, isAdmin, showNotification, userData, isReservaBloqueada, mostrarAlertaRestriccion]);

    return (
        <div className={`amenidades-page-wrapper ${isCaseta ? 'is-caseta' : ''}`}>
            <header className="amenidades-header-hero">
                <div className="amenidades-header-content">
                    <div className="amenidades-header-icon">
                        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
                            <polyline points="9 22 9 12 15 12 15 22"></polyline>
                        </svg>
                    </div>
                    <div className="amenidades-header-text">
                        <h1>Reserva de Amenidades</h1>
                        <p>Selecciona la fecha y horario para tu amenidad favorita</p>
                    </div>
                    {isAdmin && !isCaseta && (
                        <Link to="/configuraciones/amenidades-horarios" className="btn-configurar-horarios">
                            Configurar horarios
                        </Link>
                    )}
                </div>
            </header>

            <div className="amenidades-date-bar-compact">
                <div className="date-bar-main">
                    <div className="date-selector-premium">
                        <label>Fecha:</label>
                        <div className="premium-datepicker-container">
                            <button className="premium-datepicker-trigger" onClick={() => setCalendarOpen(!calendarOpen)}>
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                                    <line x1="16" y1="2" x2="16" y2="6"></line>
                                    <line x1="8" y1="2" x2="8" y2="6"></line>
                                    <line x1="3" y1="10" x2="21" y2="10"></line>
                                </svg>
                                <span>{selectedDateLabel}</span>
                                <svg className={`chevron-icon ${calendarOpen ? 'open' : ''}`} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                                    <polyline points="6 9 12 15 18 9"></polyline>
                                </svg>
                            </button>

                            {calendarOpen && (
                                <div className="premium-calendar-dropdown">
                                    <CalendarContent
                                        selectedDate={selectedDate}
                                        onSelect={(date) => {
                                            setSelectedDate(date);
                                            setCalendarOpen(false);
                                        }}
                                        onClose={() => setCalendarOpen(false)}
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                </div>
                {isAdmin && !isCaseta && (
                    <p className="amenidades-hint-small">
                        ¿Necesitas ajustar horarios? Puedes gestionarlos desde la configuración.
                    </p>
                )}
            </div>

            <div className={isCaseta ? "amenidades-caseta-grid" : "amenidades-grid"}>
                {amenitiesToDisplay.map((amenidad) => {
                    const horariosDisponibles = Array.isArray(amenidad.horarios) ? amenidad.horarios : [];
                    const hasHorarios = horariosDisponibles.length > 0;
                    const reservasDeAmenidad = reservasHoy[amenidad.id] || [];
                    const reservaLookup = new Map(reservasDeAmenidad.map((entrada) => [entrada.horario, entrada]));
                    const userReservation = reservasDeAmenidad.find((entrada) => entrada.userId === currentUserId) || null;
                    const adminTargetForAmenidad = isSuperAdmin && adminReservationTarget?.amenidadId === amenidad.id
                        ? adminReservationTarget
                        : null;
                    // Los residentes pueden hacer múltiples reservas mientras no sea en el mismo horario
                    const reservarDisabled = !hasHorarios;

                    // Para caseta: calcular estadísticas rápidas
                    const horariosOcupados = reservasDeAmenidad.length;
                    const horariosLibres = hasHorarios ? horariosDisponibles.length - horariosOcupados : 0;
                    const disponibilidadPorcentaje = hasHorarios ? Math.round((horariosLibres / horariosDisponibles.length) * 100) : 0;

                    const canCancelReservation = (() => {
                        if (isSuperAdmin) {
                            return reservasDeAmenidad.length > 0;
                        }
                        return false;
                    })();

                    const cancelTooltip = (() => {
                        if (canCancelReservation) {
                            return undefined;
                        }
                        return 'No hay reservas activas para esta amenidad en la fecha seleccionada.';
                    })();

                    const horarioRangeLabel = hasHorarios
                        ? `${(horariosDisponibles[0] || '').slice(0, 5)} - ${(horariosDisponibles[horariosDisponibles.length - 1] || '').slice(-5)}`
                        : 'Por definir';

                    // Asignar colores por amenidad
                    const amenidadColors = {
                        'salon': { bg: '#f97316', light: '#fff7ed' },
                        'area_mascotas': { bg: '#f97316', light: '#fff7ed' },
                        'alberca': { bg: '#14b8a6', light: '#f0fdfa' },
                        'gimnasio': { bg: '#3b82f6', light: '#eff6ff' },
                        'areas_verdes': { bg: '#10b981', light: '#f0fdf4' },
                        'zona_juegos': { bg: '#8b5cf6', light: '#faf5ff' },
                        'casa_club': { bg: '#ec4899', light: '#fdf2f8' },
                        'estacionamiento': { bg: '#6b7280', light: '#f9fafb' },
                        'zona_deportes': { bg: '#059669', light: '#ecfdf5' },
                        'asadores': { bg: '#ea580c', light: '#fff7ed' },
                        'cancha_padel': { bg: '#0284c7', light: '#f0f9ff' },
                        'coworking': { bg: '#6366f1', light: '#eef2ff' },
                        'fogatero': { bg: '#d97706', light: '#fffbeb' },
                        'ludoteca': { bg: '#ec4899', light: '#fdf2f8' },
                        'roof_top': { bg: '#8b5cf6', light: '#faf5ff' },
                    };
                    const colorScheme = amenidadColors[amenidad.id] || { bg: '#6b7280', light: '#f9fafb' };

                    // Vista simplificada para caseta
                    if (isCaseta) {
                        return (
                            <div key={amenidad.id} className="amenidad-card">
                                <div className="card-header" style={{ background: colorScheme.bg }}>
                                    <span className="card-icon">{amenidad.icon}</span>
                                    <div className="card-header-text">
                                        <h3>{amenidad.name}</h3>
                                        <p className="card-hours">Acceso y monitoreo</p>
                                    </div>
                                </div>
                                <div className="card-body-reservable">
                                    <div className="caseta-card-disponibilidad">
                                        <div className="disponibilidad-badge" style={{ color: colorScheme.bg }}>
                                            {disponibilidadPorcentaje}% Disponible
                                        </div>
                                        <div className="disponibilidad-detalles">
                                            <span className="libres">{horariosLibres} Libres</span>
                                            <span className="separador">•</span>
                                            <span className="ocupados">{horariosOcupados} Ocupados</span>
                                        </div>
                                    </div>

                                    <div className="caseta-card-horario">
                                        <FaClock style={{ marginRight: '8px', color: colorScheme.bg }} />
                                        <strong>Horario:</strong> {horarioRangeLabel}
                                    </div>

                                    {reservasDeAmenidad.length > 0 && (
                                        <div className="caseta-reservas-list" style={{ marginTop: '1.5rem' }}>
                                            <strong style={{ fontSize: '0.75rem', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Próximas Reservas</strong>
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '10px' }}>
                                                {reservasDeAmenidad.map((reserva, idx) => (
                                                    <div key={idx} className="caseta-reserva-item">
                                                        <span className="horario-reserva" style={{ color: colorScheme.bg }}>{reserva.horario}</span>
                                                        <span className="nombre-reserva">{reserva.createdByName || 'Sin nombre'}</span>
                                                        {reserva.casaNumero && <span className="casa-reserva">Casa {reserva.casaNumero}</span>}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    }



                    return (
                        <div key={amenidad.id} className="amenidad-card">
                            <div className="card-header" style={{ backgroundColor: colorScheme.bg }}>
                                <span className="card-icon">{amenidad.icon}</span>
                                <div className="card-header-text">
                                    <h3>{amenidad.name}</h3>
                                    <p className="card-hours">Horario: {horarioRangeLabel}</p>
                                </div>
                            </div>

                            {amenidad.type === 'reservable' && (
                                <div className="card-body-reservable">
                                    <div className="dias-semana">
                                        {WEEKDAY_BADGES.map((badge) => {
                                            const today = new Date();
                                            const currentDay = today.getDay();
                                            const daysFromToday = badge.day - currentDay;
                                            const targetDate = new Date(today);
                                            targetDate.setDate(today.getDate() + daysFromToday);
                                            const targetDateString = formatDateForInput(targetDate);

                                            return (
                                                <button
                                                    key={badge.day}
                                                    type="button"
                                                    className={`dia-semana-btn ${badge.day === activeWeekday ? 'dia-activo' : ''}`}
                                                    onClick={() => {
                                                        setSelectedDate(targetDateString);
                                                    }}
                                                >
                                                    {badge.label}
                                                </button>
                                            );
                                        })}
                                    </div>

                                    {isCalendarioBloqueado ? (
                                        <div style={{
                                            background: '#f8fafc',
                                            border: '1.5px dashed #cbd5e1',
                                            borderRadius: '12px',
                                            padding: '24px 16px',
                                            textAlign: 'center',
                                            color: '#64748b',
                                            margin: '12px 0'
                                        }}>
                                            <FaLock style={{ color: '#ef4444', fontSize: '1.4rem', marginBottom: '8px' }} />
                                            <p style={{ margin: 0, fontWeight: 700, fontSize: '0.92rem', color: '#1e293b' }}>
                                                Calendario y Disponibilidad Inhabilitados
                                            </p>
                                            <p style={{ margin: '4px 0 0 0', fontSize: '0.82rem', color: '#64748b' }}>
                                                La consulta de horarios está inhabilitada por adeudo pendiente.
                                            </p>
                                        </div>
                                    ) : hasHorarios ? (
                                        <div className="horarios-list">
                                            {horariosDisponibles.map((horario) => {
                                                const reservaInfo = reservaLookup.get(horario) || null;
                                                const estaReservado = Boolean(reservaInfo);
                                                const tooltipText = estaReservado ? buildReservationTooltip(reservaInfo, isAdmin, currentUserId) : undefined;
                                                const isTargetReservation = isAdmin && reservaInfo
                                                    && adminReservationTarget?.reservationId === reservaInfo.reservationId;
                                                const adminCanSelect = isSuperAdmin && estaReservado;

                                                const slotClassName = [
                                                    'horario-slot',
                                                    estaReservado ? 'ocupado' : '',
                                                    adminCanSelect ? 'admin-targetable-slot' : '',
                                                ].filter(Boolean).join(' ');
                                                const buttonClassName = [
                                                    'horario-btn',
                                                    estaReservado ? 'reservado' : '',
                                                    reservaInfo?.userId === currentUserId ? 'propio' : '',
                                                    adminCanSelect ? 'admin-targetable' : '',
                                                    isTargetReservation ? 'admin-selected' : '',
                                                ].filter(Boolean).join(' ');

                                                return (
                                                    <div key={horario} className={slotClassName} title={tooltipText}>
                                                        <button
                                                            type="button"
                                                            className={buttonClassName}
                                                            disabled={estaReservado}
                                                            onClick={() => {
                                                                if (estaReservado) {
                                                                    if (adminCanSelect && reservaInfo) {
                                                                        setAdminReservationTarget((prev) => {
                                                                            if (prev?.reservationId === reservaInfo.reservationId) {
                                                                                return null;
                                                                            }
                                                                            return {
                                                                                ...reservaInfo,
                                                                                amenidadId: amenidad.id,
                                                                                amenidadNombre: amenidad.name || amenidad.id,
                                                                            };
                                                                        });
                                                                    }
                                                                    return;
                                                                }
                                                                if (isReservaBloqueada) {
                                                                    mostrarAlertaRestriccion('La reserva de amenidades está temporalmente inhabilitada por adeudo pendiente. Regulariza tu cuenta en Finanzas.');
                                                                    return;
                                                                }
                                                                handleOpenModal(amenidad, horario);
                                                            }}
                                                            aria-label={estaReservado
                                                                ? `${horario} reservado${reservaInfo?.casaNumero ? ` por Casa ${reservaInfo.casaNumero}` : ''}`
                                                                : `Horario disponible ${horario}`}
                                                        >
                                                            {horario}
                                                        </button>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    ) : (
                                        <div className="amenidad-no-horarios">
                                            No hay horarios configurados para esta amenidad. Pide a tu administrador que los defina en configuraciones.
                                        </div>
                                    )}
                                </div>
                            )}

                            {amenidad.type === 'disponibilidad' && (
                                <div className="card-body-disponibilidad">
                                    <div className="dias-semana">
                                        {WEEKDAY_BADGES.map((badge) => {
                                            const today = new Date();
                                            const currentDay = today.getDay();
                                            const daysFromToday = badge.day - currentDay;
                                            const targetDate = new Date(today);
                                            targetDate.setDate(today.getDate() + daysFromToday);
                                            const targetDateString = formatDateForInput(targetDate);

                                            return (
                                                <button
                                                    key={badge.day}
                                                    type="button"
                                                    className={`dia-semana-btn ${badge.day === activeWeekday ? 'dia-activo' : ''}`}
                                                    onClick={() => {
                                                        setSelectedDate(targetDateString);
                                                    }}
                                                >
                                                    {badge.label}
                                                </button>
                                            );
                                        })}
                                    </div>
                                    {isCalendarioBloqueado ? (
                                        <div style={{
                                            background: '#f8fafc',
                                            border: '1.5px dashed #cbd5e1',
                                            borderRadius: '12px',
                                            padding: '24px 16px',
                                            textAlign: 'center',
                                            color: '#64748b',
                                            margin: '12px 0'
                                        }}>
                                            <FaLock style={{ color: '#ef4444', fontSize: '1.4rem', marginBottom: '8px' }} />
                                            <p style={{ margin: 0, fontWeight: 700, fontSize: '0.92rem', color: '#1e293b' }}>
                                                Calendario y Disponibilidad Inhabilitados
                                            </p>
                                            <p style={{ margin: '4px 0 0 0', fontSize: '0.82rem', color: '#64748b' }}>
                                                La consulta de horarios está inhabilitada por adeudo pendiente.
                                            </p>
                                        </div>
                                    ) : hasHorarios ? (
                                        <div className="horarios-list">
                                            {horariosDisponibles.map((horario) => {
                                                const reservaInfo = reservaLookup.get(horario) || null;
                                                const tooltipText = reservaInfo ? buildReservationTooltip(reservaInfo, isAdmin, currentUserId) : undefined;
                                                return (
                                                    <div
                                                        key={horario}
                                                        className={`horario-slot ${reservaInfo ? 'ocupado' : ''}`}
                                                        title={tooltipText}
                                                    >
                                                        <button
                                                            type="button"
                                                            className={`horario-btn ${reservaInfo ? 'reservado' : ''} ${reservaInfo?.userId === currentUserId ? 'propio' : ''}`}
                                                            disabled
                                                            aria-label={`Horario ${horario}`}
                                                        >
                                                            {horario}
                                                        </button>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    ) : (
                                        <div className="amenidad-no-horarios">
                                            No hay horarios configurados para esta amenidad. Configúralos en la sección de horarios.
                                        </div>
                                    )}
                                </div>
                            )}

                            {!isCaseta && (
                                <div className="amenidad-card-actions">
                                    {!isAdmin && (
                                        <button
                                            className={`btn-reservar-principal ${isReservaBloqueada ? 'btn-bloqueado-mora' : ''}`}
                                            onClick={() => {
                                                if (isReservaBloqueada) {
                                                    mostrarAlertaRestriccion('La reserva de amenidades está temporalmente inhabilitada por adeudo pendiente. Regulariza tu cuenta en Finanzas.');
                                                    return;
                                                }
                                                handleOpenModal(amenidad);
                                            }}
                                            disabled={reservarDisabled && !isReservaBloqueada}
                                            style={isReservaBloqueada ? {
                                                background: '#fee2e2',
                                                color: '#ef4444',
                                                border: '1.5px dashed #fca5a5',
                                                cursor: 'pointer',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                gap: '8px',
                                                fontWeight: 700
                                            } : undefined}
                                        >
                                            {isReservaBloqueada ? (
                                                <>
                                                    <FaLock /> Inhabilitado por mora
                                                </>
                                            ) : (
                                                'Nueva Reserva'
                                            )}
                                        </button>
                                    )}
                                    {isSuperAdmin && (
                                        <>
                                            <button
                                                className="btn-reservar-principal"
                                                onClick={() => handleOpenModal(amenidad)}
                                                disabled={reservarDisabled}
                                            >
                                                Nueva Reserva
                                            </button>
                                            <button
                                                type="button"
                                                className="btn-gestionar-reservas"
                                                disabled={!canCancelReservation}
                                                onClick={() => {
                                                    if (reservasDeAmenidad.length === 0) {
                                                        showNotification('No hay reservas para administrar en esta amenidad.', 'info');
                                                        return;
                                                    }
                                                    handleOpenReservationManager(amenidad);
                                                }}
                                                title={cancelTooltip}
                                            >
                                                Gestionar Reservas
                                            </button>
                                        </>
                                    )}
                                </div>
                            )}

                        </div>
                    );
                })}

                {!isLoadingAmenidades && amenitiesToDisplay.length === 0 && (
                    <div className="amenidades-empty">
                        No hay amenidades configuradas para tu fraccionamiento. Contacta a tu administrador para más información.
                    </div>
                )}

                {isLoadingAmenidades && (
                    <div className="amenidades-empty">Cargando amenidades configuradas…</div>
                )}
            </div>

            <ReservaAmenidadModal
                open={isModalOpen}
                amenidad={selectedAmenidad}
                onClose={handleCloseModal}
                onSubmit={modalMode === 'edit' ? handleUpdateReserva : handleSubmitReserva}
                isAdmin={isAdmin}
                fraccionamientoId={fraccionamientoId}
                allowEdit={modalMode === 'edit' ? isAdmin : !isReservaBloqueada}
                defaultDate={reservationToEdit?.fecha || selectedDate}
                currentUserId={currentUserId}
                defaultHorario={preselectedHorario}
                mode={modalMode}
                existingReservation={reservationToEdit}
            />

            <SuperAdminReservationsModal
                open={Boolean(reservationManagerAmenidad)}
                amenidad={reservationManagerAmenidad}
                reservations={reservationManagerAmenidad ? (reservasHoy[reservationManagerAmenidad.id] || []) : []}
                selectedDate={selectedDate}
                onClose={handleCloseReservationManager}
                onEdit={handleStartEditReservation}
                onDelete={(reservation) => {
                    if (!reservationManagerAmenidad) {
                        return;
                    }
                    handleCancelReservation(reservationManagerAmenidad, {
                        ...reservation,
                        fecha: reservation.fecha || selectedDate,
                        amenidadId: reservationManagerAmenidad.id,
                    });
                }}
            />
        </div>
    );
};

const ReservaAmenidadModal = ({
    open,
    amenidad,
    onClose,
    onSubmit,
    isAdmin,
    fraccionamientoId,
    allowEdit = true,
    defaultDate,
    defaultHorario = '',
    currentUserId = null,
    mode = 'create',
    existingReservation = null,
}) => {
    const [fecha, setFecha] = useState('');
    const [horario, setHorario] = useState('');
    const [area, setArea] = useState('');
    const [notas, setNotas] = useState('');
    const [reservedSlots, setReservedSlots] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const isEditMode = mode === 'edit';
    const existingHorario = normalizeSlot(existingReservation?.horario) || existingReservation?.horario || '';

    // Calcular fecha mínima: hoy para todos (residentes y admins)
    const minDate = useMemo(() => formatDateForInput(new Date()), []);

    useEffect(() => {
        if (!open || !amenidad) {
            return;
        }
        // Para crear (create), usar defaultDate (selectedDate del día seleccionado)
        // Para editar (edit), usar la fecha existente
        let initialDate;
        if (isEditMode) {
            initialDate = existingReservation?.fecha || defaultDate || formatDateForInput(new Date());
        } else {
            // En modo create, usar defaultDate que refleja el día seleccionado por el usuario
            initialDate = defaultDate || minDate;
        }
        setFecha(initialDate);
        const initialHorario = normalizeSlot(defaultHorario) || existingHorario || defaultHorario || '';
        setHorario(initialHorario);
        setArea(existingReservation?.area || amenidad?.name || '');
        setNotas(existingReservation?.notas || '');
        setReservedSlots([]);
    }, [open, amenidad, defaultDate, defaultHorario, existingReservation, existingHorario, isEditMode, minDate]);

    useEffect(() => {
        if (!open || !fraccionamientoId || !amenidad?.id || !fecha) {
            setReservedSlots([]);
            return undefined;
        }

        const reservasRef = collection(db, 'fraccionamientos', fraccionamientoId, 'reservasAmenidades');
        const reservasQuery = query(
            reservasRef,
            where('amenidadId', '==', amenidad.id),
            where('fecha', '==', fecha),
            where('estado', '==', 'activo'),
        );

        const unsubscribe = onSnapshot(reservasQuery, (snapshot) => {
            const slots = Array.from(
                new Set(
                    snapshot.docs
                        .map((docSnap) => normalizeSlot(docSnap.data()?.horario))
                        .filter(Boolean),
                ),
            ).sort((a, b) => a.slice(0, 5).localeCompare(b.slice(0, 5)));
            setReservedSlots(slots);
        }, (error) => {
            console.error('Error al cargar los horarios reservados:', error);
            setReservedSlots([]);
        });

        return () => unsubscribe();
    }, [open, fraccionamientoId, amenidad?.id, fecha]);

    useEffect(() => {
        if (!open || !allowEdit || isEditMode) {
            return;
        }
        const horariosDisponibles = Array.isArray(amenidad?.horarios) ? amenidad.horarios : [];
        if (!horariosDisponibles.length) {
            return;
        }

        if (!horario) {
            const available = horariosDisponibles.find((slot) => !reservedSlots.includes(slot)) || '';
            if (available && available !== horario) {
                setHorario(available);
            }
            return;
        }

        if (reservedSlots.includes(horario)) {
            const available = horariosDisponibles.find((slot) => !reservedSlots.includes(slot)) || '';
            if (available && available !== horario) {
                setHorario(available);
            }
        }
    }, [open, allowEdit, amenidad, horario, reservedSlots, isEditMode]);

    if (!open || !amenidad) {
        return null;
    }

    const horariosDisponibles = Array.isArray(amenidad.horarios) ? amenidad.horarios : [];
    const canSubmit = allowEdit && !isSubmitting && fecha && horario && area.trim() && horariosDisponibles.length > 0;
    const horarioPermitido = isEditMode && existingReservation?.fecha === fecha ? existingHorario : '';

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (!canSubmit) {
            return;
        }
        setIsSubmitting(true);
        try {
            const success = await onSubmit({
                amenidadId: amenidad.id,
                amenidadNombre: amenidad.name,
                fecha,
                horario,
                area: area.trim(),
                notas: notas.trim(),
                reservationId: existingReservation?.id || existingReservation?.reservationId || null,
                userId: existingReservation?.userId || currentUserId || null,
            });
            if (success) {
                onClose();
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    return createPortal(
        (
            <div className="amenidades-modal-backdrop" role="dialog" aria-modal="true">
                <div className="amenidades-modal">
                    <div className="amenidades-modal-header">
                        <h2>{isEditMode ? `Editar reserva · ${amenidad.name}` : `Reservar ${amenidad.name}`}</h2>
                        <button type="button" className="amenidades-modal-close" onClick={onClose} aria-label="Cerrar">
                            ×
                        </button>
                    </div>
                    <form className="amenidades-modal-form" onSubmit={handleSubmit}>
                        <div className="amenidades-modal-field">
                            <label htmlFor="amenidad-area">Área</label>
                            <input
                                id="amenidad-area"
                                type="text"
                                value={area}
                                placeholder="Nombre del área"
                                readOnly
                                disabled={!allowEdit || isSubmitting}
                            />
                        </div>

                        <div className="amenidades-modal-field amenidades-modal-inline">
                            <div className="amenidades-modal-subfield">
                                <label htmlFor="amenidad-fecha">Fecha *</label>
                                <input
                                    id="amenidad-fecha"
                                    type="date"
                                    value={fecha}
                                    min={minDate}
                                    onChange={(event) => {
                                        setFecha(event.target.value);
                                        setHorario('');
                                    }}
                                    disabled={!allowEdit || isSubmitting}
                                    required
                                />
                            </div>
                            <div className="amenidades-modal-subfield">
                                <label htmlFor="amenidad-horario">Horario *</label>
                                <select
                                    id="amenidad-horario"
                                    value={horario}
                                    onChange={(event) => setHorario(event.target.value)}
                                    disabled={!allowEdit || isSubmitting || horariosDisponibles.length === 0}
                                    required
                                >
                                    <option value="">Selecciona un horario</option>
                                    {horariosDisponibles.map((opcion) => {
                                        const disabled = reservedSlots.includes(opcion)
                                            && opcion !== horarioPermitido;
                                        return (
                                            <option key={opcion} value={opcion} disabled={disabled}>
                                                {opcion}
                                            </option>
                                        );
                                    })}
                                    {horariosDisponibles.length === 0 && <option value="" disabled>No hay horarios disponibles</option>}
                                </select>
                                {horariosDisponibles.length === 0 && (
                                    <p className="amenidades-modal-hint">
                                        Esta amenidad no tiene horarios configurados en este momento. Solicita a la administración que los actualice.
                                    </p>
                                )}
                                {reservedSlots.length > 0 && horariosDisponibles.length > 0 && (
                                    <p className="amenidades-modal-hint">Horarios ocupados: {reservedSlots.join(', ')}</p>
                                )}
                            </div>
                        </div>

                        <div className="amenidades-modal-field">
                            <label htmlFor="amenidad-notas">Notas internas</label>
                            <textarea
                                id="amenidad-notas"
                                rows={2}
                                value={notas}
                                onChange={(event) => setNotas(event.target.value)}
                                placeholder="Detalles adicionales o indicaciones"
                                disabled={!allowEdit || isSubmitting}
                            />
                        </div>

                        <div className="amenidades-modal-actions">
                            <button type="button" className="secondary" onClick={onClose} disabled={isSubmitting}>
                                Cancelar
                            </button>
                            <button type="submit" className="primary" disabled={!canSubmit}>
                                {isSubmitting ? 'Guardando...' : 'Guardar reserva'}
                            </button>
                        </div>
                        {!isAdmin && (
                            <p className="amenidades-modal-footer-note">
                                <strong>Importante:</strong> Solo puedes hacer reservas para hoy y mañana. Solo una reserva por día para la misma amenidad (aunque sea otro horario). No puedes reservar 2 amenidades diferentes en el mismo horario del mismo día. Una vez registrada tu reserva, contacta a tu administrador para modificaciones o cancelaciones.
                            </p>
                        )}
                    </form>
                </div>
            </div>
        ),
        document.body,
    );
};

const SuperAdminReservationsModal = ({
    open,
    amenidad,
    reservations,
    selectedDate,
    onClose,
    onEdit,
    onDelete,
}) => {
    const [reservationToDelete, setReservationToDelete] = useState(null);

    if (!open || !amenidad) {
        return null;
    }

    const reservationList = Array.isArray(reservations) ? reservations : [];
    const headerDateLabel = formatDateLabel(selectedDate);

    return createPortal(
        (
            <div className="amenidades-modal-backdrop" role="dialog" aria-modal="true">
                <div className="amenidades-modal superadmin-reservas-modal">
                    <div className="amenidades-modal-header">
                        <h2>Reservas · {amenidad.name}</h2>
                        <button type="button" className="amenidades-modal-close" onClick={onClose} aria-label="Cerrar">
                            ×
                        </button>
                    </div>
                    <div className="superadmin-reservas-body">
                        <p className="superadmin-reservas-date">Fecha seleccionada: {headerDateLabel}</p>
                        {reservationList.length === 0 ? (
                            <p className="superadmin-reservas-empty">No hay reservas activas para esta fecha.</p>
                        ) : (
                            <ul className="superadmin-reservas-list">
                                {reservationList.map((reserva) => (
                                    <li key={reserva.reservationId} className="superadmin-reserva-item">
                                        <div className="superadmin-reserva-info">
                                            <span className="superadmin-reserva-horario">{reserva.horario}</span>
                                            <span className="superadmin-reserva-nombre">{reserva.createdByName || 'Sin nombre'}</span>
                                            {reserva.casaNumero && (
                                                <span className="superadmin-reserva-casa">Casa {reserva.casaNumero}</span>
                                            )}
                                        </div>
                                        <div className="superadmin-reserva-actions">
                                            <button
                                                type="button"
                                                className="superadmin-reserva-btn"
                                                onClick={() => onEdit(amenidad, reserva)}
                                            >
                                                Editar
                                            </button>
                                            <button
                                                type="button"
                                                className="superadmin-reserva-btn danger"
                                                onClick={() => setReservationToDelete(reserva)}
                                            >
                                                Eliminar
                                            </button>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                    <ConfirmDialog
                        open={Boolean(reservationToDelete)}
                        title="Cancelar reserva"
                        message="La reserva seleccionada se cancelará y quedará disponible para otros residentes."
                        cancelLabel="Conservar"
                        confirmLabel="Cancelar reserva"
                        tone="danger"
                        onCancel={() => setReservationToDelete(null)}
                        onConfirm={() => {
                            if (reservationToDelete) {
                                onDelete(reservationToDelete);
                            }
                            setReservationToDelete(null);
                        }}
                    />
                </div>
            </div>
        ),
        document.body,
    );
};

const CalendarContent = ({ selectedDate, onSelect, onClose }) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const [viewDate, setViewDate] = useState(() => {
        if (!selectedDate) return new Date();
        const [y, m, d] = selectedDate.split('-').map(Number);
        return new Date(y, m - 1, d);
    });

    const monthNames = ["enero", "febrero", "marzo", "abril", "mayo", "junio", "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre"];
    const daysShort = ["dom", "lun", "mar", "mié", "jue", "vie", "sáb"];

    const currentYear = viewDate.getFullYear();
    const currentMonth = viewDate.getMonth();

    const firstDayOfMonth = new Date(currentYear, currentMonth, 1).getDay();
    const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();

    const prevMonthDays = new Date(currentYear, currentMonth, 0).getDate();

    const handlePrevMonth = () => setViewDate(new Date(currentYear, currentMonth - 1, 1));
    const handleNextMonth = () => setViewDate(new Date(currentYear, currentMonth + 1, 1));

    const isSelected = (d) => {
        if (!selectedDate) return false;
        const [y, m, day] = selectedDate.split('-').map(Number);
        return y === currentYear && m === currentMonth + 1 && d === day;
    };

    const isToday = (d) => {
        return today.getFullYear() === currentYear && today.getMonth() === currentMonth && today.getDate() === d;
    };

    const days = [];
    // Padding from prev month
    for (let i = firstDayOfMonth - 1; i >= 0; i--) {
        days.push({ day: prevMonthDays - i, current: false });
    }
    // Current month days
    for (let i = 1; i <= daysInMonth; i++) {
        days.push({ day: i, current: true });
    }
    // Padding for next month
    const remaining = 42 - days.length;
    for (let i = 1; i <= remaining; i++) {
        days.push({ day: i, current: false });
    }

    return (
        <div className="calendar-content-premium">
            <div className="calendar-header-nav">
                <button onClick={handlePrevMonth} className="nav-btn">&lt;</button>
                <div className="current-month-label">
                    {monthNames[currentMonth]} de {currentYear}
                </div>
                <button onClick={handleNextMonth} className="nav-btn">&gt;</button>
            </div>
            <div className="calendar-days-grid">
                {daysShort.map(d => <div key={d} className="day-name">{d}</div>)}
                {days.map((d, idx) => (
                    <div
                        key={idx}
                        className={`day-cell ${!d.current ? 'other-month' : ''} ${d.current && isSelected(d.day) ? 'selected' : ''} ${d.current && isToday(d.day) ? 'today' : ''}`}
                        onClick={() => d.current && onSelect(`${currentYear}-${String(currentMonth + 1).padStart(2, '0')}-${String(d.day).padStart(2, '0')}`)}
                    >
                        {d.day}
                    </div>
                ))}
            </div>
            <div className="calendar-footer">
                <button className="limpiar-btn" onClick={() => onSelect(formatDateForInput(new Date()))}>Hoy</button>
            </div>
        </div>
    );
};

export default Amenidades;
